import { t } from "@lingui/macro";
import graphql from "babel-plugin-relay/macro";
import { useSetAtom } from "jotai";
import { useCallback } from "react";

import { useToast } from "@whoosh/design-system/src/components/overlay/Toast/useToast";

import { useMutationWithError } from "../../../utils/hooks/useMutationWithError";
import { OverlayLoading_atom } from "../../OverlayLoader/atoms";
import { LockedSlots_atom } from "../atoms";
import { LockTimeSlotsInput } from "./__generated__/useLockSlotMutation.graphql";

export const useLockSlots = () => {
  const [lockSlots, isLocking] = useMutationWithError(
    useLockSlotMutationString
  );
  const updateLockSlotsInfo = useSetAtom(LockedSlots_atom);
  const setLoading = useSetAtom(OverlayLoading_atom);
  const { showToast } = useToast();

  // TODO: Fix any!
  const onCompleted = useCallback(
    (res: any, successCallback: any) => {
      setLoading(false);
      const slotIds = res.responsePayload.slots.map((slot: any) => slot.id);
      const nowDate = Date.now();
      updateLockSlotsInfo({ slotIds: slotIds, lockTime: nowDate });
      if (!!successCallback) {
        successCallback();
      }
    },
    [setLoading, updateLockSlotsInfo]
  );

  const onCompletedErrorCustom = useCallback(
    (errors: any, errorCallback?: () => void) => {
      setLoading(false);
      const remainingErrors: any = [];
      errors.forEach((e: any) => {
        if (e.code === "already_locked" || e.code === "already_taken") {
          showToast({
            description: t`Selected time is no longer available.`,
            semanticStyle: "error",
            toastId: "tee-time-error",
            testID: "lock-slot-error-toast",
          });
        } else {
          remainingErrors.push(e);
        }
      });

      if (errorCallback) {
        errorCallback();
      }

      return remainingErrors;
    },
    [setLoading, showToast]
  );

  const onErrorCustom = useCallback(
    (errorCallback?: () => void) => {
      if (errorCallback) {
        errorCallback();
      }
      setLoading(false);
    },
    [setLoading]
  );

  const doLockSlot = useCallback(
    (
      input: LockTimeSlotsInput,
      successCallback?: () => void,
      errorCallback?: () => void,
      showOverlay: boolean = true
    ) => {
      if (showOverlay) setLoading(true);
      lockSlots({
        variables: { input },
        onCompleted: (res: any) => {
          onCompleted(res, successCallback);
        },
        onCompletedErrorCustom: (errors: any) =>
          onCompletedErrorCustom(errors, errorCallback),
        onErrorCustom: (error: any) => onErrorCustom(errorCallback),
      });
    },
    [lockSlots, onCompleted, onCompletedErrorCustom, onErrorCustom, setLoading]
  );

  const handleLockSlot = useCallback(
    (
      input: LockTimeSlotsInput,
      successCallback: () => void,
      errorCallback?: () => void,
      showOverlay: boolean = true
    ) => {
      doLockSlot(input, successCallback, errorCallback, showOverlay);
    },
    [doLockSlot]
  );

  return [handleLockSlot, isLocking] as const;
};

const useLockSlotMutationString = graphql`
  mutation useLockSlotMutation($input: LockTimeSlotsInput!) {
    responsePayload: lockTimeSlots(input: $input) {
      errors {
        code

        message
        field
      }
      success
      slots {
        id
        # ...TimeSlotRow_Timeslot TODO: REMOVING THIS FOR NOW. NEED TO FIGURE OUT HOW TO GET MEMBERSHIP_ID FOR THIS FRAGMENT
        ...TeeSheetAgendaConsistency_timeSlot
      }
    }
  }
`;
